// src/components/dashboard/InvestmentEditModal.tsx

import React, { useState, useEffect } from 'react';
import { X, Save, DollarSign } from 'lucide-react';
import MonthYearSelector from './MonthYearSelector.tsx';
import { getDashboardThemeClasses } from './dashboard/dashboardTheme.ts';

// Definimos a interface das props
interface InvestmentEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Função onSave que recebe:
   *  - numericValue: valor do investimento (ex.: 123.45)
   *  - referenceMonth: string no formato YYYY-MM (ex.: "2023-07")
   */
  onSave: (numericValue: number, referenceMonth: string) => void;
  currentValue: number;  // valor atual do investimento, se houver
  campaignId: string;    // ID externo do anúncio (source_id)
  title?: string;        // Título opcional do modal
  isDark?: boolean;
}

const InvestmentEditModal: React.FC<InvestmentEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentValue,
  campaignId,
  title,
  isDark = false,
}) => {
  const theme = getDashboardThemeClasses(isDark);
  // Estado para o valor numérico do investimento
  const [numericValue, setNumericValue] = useState<number>(0);
  // Estado para o valor formatado exibido no input
  const [formattedValue, setFormattedValue] = useState<string>('');
  // Estado para possíveis erros de validação
  const [error, setError] = useState<string | null>(null);

  // Estado para o mês/ano de referência (ex.: "2023-07")
  const [referenceMonth, setReferenceMonth] = useState<string>('');

  // Função para formatar número -> texto no formato monetário brasileiro
  const formatCurrency = (value: number): string => {
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Ao abrir o modal (isOpen = true), inicializamos os campos:
  //  - o valor (numericValue) e sua versão formatada
  //  - o mês de referência = mês atual por padrão
  useEffect(() => {
    if (isOpen) {
      setNumericValue(currentValue);
      setFormattedValue(formatCurrency(currentValue));
      setError(null);

      // Por padrão, sugerimos o mês atual no formato YYYY-MM
      const today = new Date();
      const year = today.getFullYear();
      // Retorna '07' para julho, '08' para agosto etc.
      const month = String(today.getMonth() + 1).padStart(2, '0');
      setReferenceMonth(`${year}-${month}`);
    }
  }, [isOpen, currentValue]);

  // Lidar com a mudança de texto no input monetário
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, ''); // remove tudo que não seja dígito
    if (!rawValue) {
      setNumericValue(0);
      setFormattedValue('0,00');
      return;
    }
    // Ex.: rawValue = "12345" => 123,45
    const cents = parseInt(rawValue, 10) / 100;
    setNumericValue(cents);
    setFormattedValue(formatCurrency(cents));
    setError(null);
  };

  // Lidar com a mudança no seletor de mês/ano
  const handleMonthChange = (value: string) => {
    setReferenceMonth(value);
    setError(null);
  };

  // Validação e persistência ao clicar "Salvar"
  const handleSave = () => {
    if (numericValue < 0) {
      setError('O valor do investimento não pode ser negativo.');
      return;
    }
    if (!referenceMonth) {
      setError('Selecione o mês de referência.');
      return;
    }
    setError(null);

    onSave(numericValue, referenceMonth);
  };

  // Fechar modal com ESC
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  // Interceptar Enter no input e disparar handleSave
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      {/* Overlay de fundo com animação */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Container do Modal com animação */}
      <div className={`relative z-10 w-full max-w-md overflow-hidden rounded-2xl border shadow-xl ${theme.surface}`}>
        {/* Header do Modal */}
        <div className={`flex items-center justify-between border-b px-4 py-3 ${theme.border}`}>
          <h3 className={`text-lg font-semibold ${theme.primaryText}`}>
            {title || `Editar Investimento - Anúncio ${campaignId}`}
          </h3>
          <button 
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className={`rounded-lg p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary ${isDark ? 'text-[#A6B2C3] hover:bg-[#27364D]/45 hover:text-[#F8FAFC]' : 'text-sminy-muted hover:bg-slate-100 hover:text-sminy-text-primary'}`}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        
        {/* Conteúdo do Modal */}
        <div className="p-4 space-y-5">
          {/* Identidade externa do anúncio; nunca é o ID interno do banco. */}
          <div className={`rounded-xl border px-3 py-2.5 ${isDark ? 'border-[#27364D] bg-[#071225]/45' : 'border-sminy-border bg-slate-50'}`}>
            <p aria-label={`ID do anúncio: ${campaignId}`} className={`text-sm ${theme.secondaryText}`}>
              <span className="font-medium">ID do anúncio:</span> {campaignId}
            </p>
          </div>
          
          {/* Input do Valor */}
          <div>
            <label htmlFor="investment-value" className={`mb-1 block text-sm font-medium ${theme.primaryText}`}>
              Valor do Investimento
            </label>
            <div data-testid="investment-value-field" className="relative rounded-xl">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className={`h-5 w-5 ${theme.mutedText}`} aria-hidden="true" />
              </div>
              <input
                type="text"
                id="investment-value"
                className={`block w-full rounded-xl border py-3 pl-10 outline-none focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/15 ${theme.input} ${error ? 'border-rose-300' : ''}`}
                value={formattedValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                autoFocus
                placeholder="0,00"
              />
            </div>
          </div>

          {/* Seletor de Mês/Ano de referência */}
          <div>
            <label htmlFor="reference-month" className={`mb-1 block text-sm font-medium ${theme.primaryText}`}>
              Mês de Referência
            </label>
            <MonthYearSelector 
              value={referenceMonth}
              onChange={handleMonthChange}
              error={error ? true : false}
            />
          </div>

          {/* Mensagem de erro, se houver */}
          {error && (
            <div className={`rounded-xl border p-3 ${isDark ? 'border-rose-500/30 bg-rose-500/10' : 'border-rose-200 bg-rose-50'}`}>
              <p className={`flex items-start text-sm ${isDark ? 'text-rose-300' : 'text-rose-600'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                </svg>
                {error}
              </p>
            </div>
          )}
        </div>
        
        {/* Footer do Modal */}
        <div className={`flex justify-end gap-2 border-t px-4 py-3 ${theme.border}`}>
          <button
            type="button"
            className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary ${theme.control}`}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="inline-flex items-center rounded-xl bg-sminy-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sminy-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary focus-visible:ring-offset-2"
            onClick={handleSave}
          >
            <Save className="h-4 w-4 mr-1.5" />
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvestmentEditModal;
